import { useState, useEffect, useRef } from 'react';
import { getLog, getDeals, APISLogResponse, APISDealsResponse } from '../services/apisClient'; // eslint-disable-line @typescript-eslint/no-unused-vars

export interface EnergyData {
  solarGeneration: number;
  windGeneration: number;
  batteryLevel: number;
  homeConsumption: number;
  evCharging: number;
  gridExport: number;
  weatherCondition: string;
  gridPrice: number;
}

const INITIAL_ENERGY_DATA: EnergyData = {
  solarGeneration: 4.2,
  windGeneration: 2.8,
  batteryLevel: 75,
  homeConsumption: 3.5,
  evCharging: 7.2,
  gridExport: 0,
  weatherCondition: 'sunny',
  gridPrice: 0.12,
};

const POLL_INTERVAL_MS = 2000;
const MAX_CONSECUTIVE_FAILURES = 3;

// ---------------------------------------------------------------------------
// Pure mapping: APIS /get/log + /deals -> EnergyData
// ---------------------------------------------------------------------------

export function mapAPISToEnergyData(
  log: APISLogResponse,
  deals: APISDealsResponse,
  planningBaseEVKw: number = 0,
  planningBaseHomeKw: number = 0,
): EnergyData {
  const nodes = Object.values(log);

  if (nodes.length === 0) {
    return INITIAL_ENERGY_DATA;
  }

  // Battery: average RSoC across all nodes
  const batteryLevel =
    nodes.reduce((sum, n) => sum + (n.emu.rsoc ?? 0), 0) / nodes.length;

  // Solar: sum of pvc_charge_power across all nodes (W -> kW)
  const solarGenerationW = nodes.reduce(
    (sum, n) => sum + (n.emu.pvc_charge_power ?? 0),
    0,
  );

  // Home consumption: sum of ups_output_power (load) (W -> kW).
  // Falls back to System Planning Tool configured load when APIS reports 0.
  const homeFromNodesW = nodes.reduce(
    (sum, n) => sum + (n.emu.ups_output_power ?? 0),
    0,
  );
  const homeConsumptionW = homeFromNodesW > 0 ? homeFromNodesW : planningBaseHomeKw * 1000;

  // Grid: sum of dcdc.meter.wg across nodes (W -> kW)
  // Positive = import from grid, negative = export to grid
  const gridNetW = nodes.reduce(
    (sum, n) => sum + (n.dcdc?.meter?.wg ?? 0),
    0,
  );

  // EV charging: use active P2P deal current when available, otherwise fall back
  // to the System Planning Tool configured load (planningBaseEVKw).
  const NOMINAL_DC_VOLTAGE_V = 350;
  const evFromDealsW = deals.reduce(
    (sum, d) => sum + Math.abs(d.dealGridCurrentA ?? 0) * NOMINAL_DC_VOLTAGE_V,
    0,
  );
  const evChargingW = evFromDealsW > 0 ? evFromDealsW : planningBaseEVKw * 1000;

  // Wind: APIS does not model wind turbines — keep a stable non-zero value
  // so the demo flow visualization remains informative.
  // In a real deployment, a dedicated wind measurement node would be identified.
  const WIND_BASELINE_KW = 2.8;

  // Grid price: derived from time-of-use pattern (APIS does not expose pricing)
  const hour = new Date().getHours();
  let gridPrice: number;
  if (hour >= 16 && hour <= 21) {
    gridPrice = 0.22;
  } else if (hour >= 6 && hour <= 16) {
    gridPrice = 0.14;
  } else {
    gridPrice = 0.08;
  }

  return {
    solarGeneration: Math.max(0, solarGenerationW / 1000),
    windGeneration: WIND_BASELINE_KW,
    batteryLevel: Math.max(0, Math.min(100, batteryLevel)),
    homeConsumption: Math.max(0, homeConsumptionW / 1000),
    evCharging: Math.max(0, evChargingW / 1000),
    gridExport: gridNetW < 0 ? Math.abs(gridNetW) / 1000 : 0,
    weatherCondition: solarGenerationW > 2000 ? 'sunny' : solarGenerationW > 500 ? 'cloudy' : 'night',
    gridPrice,
  };
}

// ---------------------------------------------------------------------------
// Simulation fallback (mirrors App.tsx logic for when APIS is unreachable)
// ---------------------------------------------------------------------------

function simulateEnergyData(
  prev: EnergyData,
  baseEVKw: number,
  baseHomeKw: number,
): EnergyData {
  const hour = new Date().getHours();
  const minute = new Date().getMinutes();

  const solarMultiplier =
    hour >= 6 && hour <= 18
      ? Math.sin(((hour - 6) * Math.PI) / 12) * 0.8 + 0.2
      : 0;

  let newGridPrice = prev.gridPrice;
  if (minute % 15 === 0) {
    if (hour >= 16 && hour <= 21) {
      newGridPrice = 0.18 + Math.random() * 0.08;
    } else if (hour >= 6 && hour <= 16) {
      newGridPrice = 0.12 + Math.random() * 0.04;
    } else {
      newGridPrice = 0.06 + Math.random() * 0.04;
    }
  }

  return {
    ...prev,
    solarGeneration: Math.max(0, 8 * solarMultiplier + Math.random() * 0.5 - 0.25),
    windGeneration: Math.max(0, 3 + Math.random() * 2 - 1),
    homeConsumption: baseHomeKw * (0.8 + Math.random() * 0.4),
    evCharging: baseEVKw > 0 ? baseEVKw * (0.9 + Math.random() * 0.2) : 0,
    batteryLevel: Math.max(10, Math.min(95, prev.batteryLevel + (Math.random() - 0.5) * 2)),
    gridPrice: newGridPrice,
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface APISDataResult {
  energyData: EnergyData;
  isLive: boolean;
  connectionError: string | null;
}

export function useAPISData(
  simulationBaseEVKw: number,
  simulationBaseHomeKw: number,
): APISDataResult {
  const [energyData, setEnergyData] = useState<EnergyData>(INITIAL_ENERGY_DATA);
  const [isLive, setIsLive] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const failCountRef = useRef(0);
  // Ref to avoid stale closures inside setInterval for simulation params
  const simParamsRef = useRef({ baseEVKw: simulationBaseEVKw, baseHomeKw: simulationBaseHomeKw });
  useEffect(() => {
    simParamsRef.current = { baseEVKw: simulationBaseEVKw, baseHomeKw: simulationBaseHomeKw };
  }, [simulationBaseEVKw, simulationBaseHomeKw]);

  // Track live status in a ref so simulation branch can read it without stale closure
  const isLiveRef = useRef(false);
  useEffect(() => {
    isLiveRef.current = isLive;
  }, [isLive]);

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      try {
        const [log, deals] = await Promise.all([
          getLog(),
          getDeals().catch(() => [] as APISDealsResponse),
        ]);
        if (cancelled) return;

        failCountRef.current = 0;
        if (!isLiveRef.current) {
          setIsLive(true);
          setConnectionError(null);
        }
        setEnergyData(mapAPISToEnergyData(log, deals, simParamsRef.current.baseEVKw, simParamsRef.current.baseHomeKw));
      } catch {
        if (cancelled) return;

        failCountRef.current += 1;
        if (failCountRef.current >= MAX_CONSECUTIVE_FAILURES) {
          if (isLiveRef.current) {
            setIsLive(false);
          }
          // Only update error message on first transition to failed state
          if (failCountRef.current === MAX_CONSECUTIVE_FAILURES) {
            setConnectionError('APIS backend unreachable — running in simulation mode');
          }
          // Run simulation step when APIS is down
          setEnergyData(prev =>
            simulateEnergyData(
              prev,
              simParamsRef.current.baseEVKw,
              simParamsRef.current.baseHomeKw,
            ),
          );
        }
      }
    }

    poll();
    const intervalId = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { energyData, isLive, connectionError };
}
